"use client";

import { Volume2, VolumeX } from "lucide-react";
import type { ComponentPropsWithoutRef } from "react";
import { useVideoAutoplay } from "@/components/providers/video-autoplay-provider";
import { uiHaptics } from "@/lib/ui-haptics";
import { cn } from "@/lib/utils";

/**
 * Where a media frame's speaker sits. Bottom-right is the standard (the feed's
 * Instagram corner); top-right is only for frames whose bottom edge is already
 * taken by content the speaker would cover (an experience card's venue row).
 */
export type MediaSoundPlacement = "bottom-right" | "top-right";

const PLACEMENT_CLASS: Record<MediaSoundPlacement, string> = {
  "bottom-right": "right-3 bottom-3",
  "top-right": "top-3 right-3",
};

/**
 * For a detail hero under a mobile snap-sheet: the sheet's first snap point
 * overlaps the hero by 10px (`innerHeight - heroHeight + 10`), so the standard
 * bottom-3 inset would leave the speaker 2px off the sheet's edge. Desktop
 * (lg+) has no sheet.
 */
export const MEDIA_SOUND_ABOVE_SHEET_CLASS = "bottom-[1.375rem] lg:bottom-3";

/**
 * The round glass shell every media-frame sound control uses. Exported so a
 * control that is not a mute toggle (a song with no playable preview opens its
 * sheet instead) still reads as the same button in the same corner.
 */
export function mediaSoundButtonClass(
  placement: MediaSoundPlacement = "bottom-right",
) {
  return cn(
    "absolute z-10 flex size-8 items-center justify-center rounded-full bg-black/55 text-white backdrop-blur-md transition-transform duration-200 ease-out-strong active:scale-90 disabled:opacity-50",
    PLACEMENT_CLASS[placement],
  );
}

type SoundToggleButtonProps = Omit<
  ComponentPropsWithoutRef<"button">,
  "onClick" | "children"
> & {
  muted: boolean;
  onToggle: () => void;
  /** What the sound belongs to, for the accessible name ("video", "song"). */
  label?: string;
  placement?: MediaSoundPlacement;
};

/**
 * The one mute/unmute speaker for media frames — feed posts, banners, cards,
 * upload previews. Render it absolutely inside the `relative` media frame and
 * as a SIBLING of any link or tap target (nested buttons are invalid); the
 * click never reaches the frame underneath.
 */
export function SoundToggleButton({
  muted,
  onToggle,
  label = "video",
  placement = "bottom-right",
  className,
  ...props
}: SoundToggleButtonProps) {
  return (
    <button
      type="button"
      aria-label={muted ? `Unmute ${label}` : `Mute ${label}`}
      {...props}
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
        uiHaptics.tap();
        onToggle();
      }}
      className={cn(mediaSoundButtonClass(placement), className)}
    >
      {muted ? (
        <VolumeX className="size-4" aria-hidden />
      ) : (
        <Volume2 className="size-4" aria-hidden />
      )}
    </button>
  );
}

/**
 * The speaker for media registered with the page-wide VideoAutoplayProvider.
 * On phones it flips the one global sound preference; on desktop it claims
 * audio ownership for `audioId`. The player must register under the identical
 * id.
 */
export function MediaSoundButton({
  audioId,
  label,
  placement,
  className,
}: {
  audioId: string;
  label?: string;
  placement?: MediaSoundPlacement;
  className?: string;
}) {
  const { isSoundMuted, toggleAudio } = useVideoAutoplay();
  return (
    <SoundToggleButton
      muted={isSoundMuted(audioId)}
      onToggle={() => toggleAudio(audioId)}
      label={label}
      placement={placement}
      className={className}
    />
  );
}
